/* sankeyDiagramOptionsSlice.ts */

/* Redux-Toolkit */
import {
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

/* Interfaces */
import {
  SankeyDiagramOptionsProps,
  MinMaxProps,
} from "src/shared/interfaces/insights-filter/InsightsFilterOptions";

const initialState: SankeyDiagramOptionsProps = {
  maxDepth: 2,
  width: 1,
  minMax: {
    min: 1,
    max: 100,
  },
  expandedOthers: [],
  expandedGroups: [],
  showNoValueStates: false,
};

export const sankeyDiagramOptionsSlice = createSlice({
  name: "sankeyDiagramOptions",
  initialState,
  reducers: {
    /**
     * Set `maxDepth` or number of treatment lines in Sankey diagaram.
     *
     * @param {SankeyDiagramOptionsProps} state
     * @param {number} action
     * @returns {void}
     */
    setMaxDepth: (
      state: SankeyDiagramOptionsProps,
      action: PayloadAction<number>
    ): void => {
      state.maxDepth = action.payload;
    },

    /**
     * Set `width` for filtering (zooming) in Sankey diagram.
     *
     * @param {SankeyDiagramOptionsProps} state
     * @param {number} action
     * @returns {void}
     */
    setWidth: (
      state: SankeyDiagramOptionsProps,
      action: PayloadAction<number>
    ): void => {
      state.width = action.payload;
    },

    /**
     * Set `min` and `max` values for filtering links in Sankey diagram.
     *
     * @param {SankeyDiagramOptionsProps} state
     * @param {MinMaxProps} action
     * @returns {void}
     */
    setMinMax: (
      state: SankeyDiagramOptionsProps,
      action: PayloadAction<MinMaxProps>
    ): void => {
      const { min, max } = action.payload;

      state.minMax.min = min;
      state.minMax.max = max;
    },

    /**
     * Expand links which are grouped to "Other" to show all links.
     *
     * @param {SankeyDiagramOptionsProps} state
     * @param {string} action
     * @returns {void}
     */
    expandGroupedNodes: (
      state: SankeyDiagramOptionsProps,
      action: PayloadAction<string>
    ): void => {
      const name = action.payload;

      state.expandedOthers = state.expandedOthers.includes(
        name
      )
        ? state.expandedOthers.filter((n) => n !== name)
        : [...state.expandedOthers, name];
    },
    /**
     * Toggle expansion of a treatment-line group node.
     *
     * When the group is collapsed the supplied name is added to
     * `expandedGroups`, causing the member nodes to be shown
     * individually. Dispatching again with the same name collapses
     * the group back.
     *
     * @param {SankeyDiagramOptionsProps} state
     * @param {string} action  - Full group-node name, e.g. "FCR-Benda (TL1)"
     * @returns {void}
     */
    toggleTreatmentGroup: (
      state: SankeyDiagramOptionsProps,
      action: PayloadAction<string>
    ): void => {
      const name = action.payload;
      state.expandedGroups =
        state.expandedGroups.includes(name)
          ? state.expandedGroups.filter((n) => n !== name)
          : [...state.expandedGroups, name];
    },

    /**
     * Collapse all expanded items: "Other" buckets and treatment-group nodes.
     *
     * @param {SankeyDiagramOptionsProps} state
     * @returns {void}
     */
    collapseGroupedNodes: (
      state: SankeyDiagramOptionsProps
    ): void => {
      state.expandedOthers = [];
      state.expandedGroups = [];
    },

    /**
     * Toggle whether Watch and Wait / "looked for, no value" are shown as
     * their own terminal TL1 nodes in the sankey diagram.
     *
     * @param {SankeyDiagramOptionsProps} state
     * @param {boolean} action
     * @returns {void}
     */
    setShowNoValueStates: (
      state: SankeyDiagramOptionsProps,
      action: PayloadAction<boolean>
    ): void => {
      state.showNoValueStates = action.payload;
    },

    /**
     * Reset `width`, `maxDepth`, and all expansion state to initial values.
     *
     * This is required when insights filter values are changing so that stale
     * group expansions from a previous cohort/condition do not carry over.
     *
     * @param {SankeyDiagramOptionsProps} state
     * @returns {void}
     */
    resetSankeyDiagramOptions: (
      state: SankeyDiagramOptionsProps
    ): void => {
      state.maxDepth = initialState.maxDepth;
      state.width = initialState.width;
      state.expandedOthers = initialState.expandedOthers;
      state.expandedGroups = initialState.expandedGroups;
    },
  },
});

/* Actions */
export const {
  setMaxDepth,
  setWidth,
  setMinMax,
  expandGroupedNodes,
  collapseGroupedNodes,
  toggleTreatmentGroup,
  setShowNoValueStates,
  resetSankeyDiagramOptions,
} = sankeyDiagramOptionsSlice.actions;

/* Reducer */
export default sankeyDiagramOptionsSlice.reducer;
